import React from 'react';
import { Section } from 'shared/ui/Section';
import { Field } from 'shared/ui/Field';
import { MoneyField } from 'shared/ui/MoneyField';
import { Toggle2 } from 'shared/ui/Toggle2';
import { SolutionFigure } from './SolutionFigure';
import { usePricingSection } from './usePricingSection';

function RegisteredToggle({ testId, checked, onToggle }: { testId: string; checked: boolean; onToggle: () => void }) {
  return (
    <span data-testid={testId}>
      <Toggle2 leftLabel="Un-Registered" rightLabel="Registered" ariaLabel="Registration status" isRightActive={checked} onToggle={onToggle} />
    </span>
  );
}

export function PricingSection({ id }: { id: string }) {
  const s = usePricingSection();
  // Classic's own unit labels: "($ / 10TB / month)" on a term-based software
  // model, "($)" otherwise (App.js:1281, :1298) — "$ per TB" was invented here.
  // Each label doubles as its MoneyField's `ariaLabel` so the accessible name
  // CONTAINS the visible text (WCAG 2.5.3 Label in Name), and the matching
  // `htmlFor`/`id` pair associates them so clicking the label focuses the input.
  const rateUnit = s.termBased ? '$ / 10 TB / month' : '$';
  const ssdLabel = `SSD Software Price (${rateUnit})`;
  const hddLabel = `HDD Software Price (${rateUnit})`;

  return (
    <Section.Root id={id} defaultOpen>
      <Section.Bar title="Pricing" role="Solution price and rates" summary={s.isCalculating ? '…' : s.summary} />
      <Section.Body>
        <h4 className="mb-2 font-display text-xs text-ink-muted">Solution</h4>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {s.figures.map((f) => (
            <SolutionFigure key={f.id} label={f.label} value={f.value} current={f.current} tone={f.tone} loading={s.isCalculating} />
          ))}
        </div>
        {s.registeredIn === 'solution' && (
          <div className="mt-3"><RegisteredToggle testId="registered-solution" checked={s.registered} onToggle={s.onRegisteredToggle} /></div>
        )}

        {s.showRates && (
          <>
            <h4 className="mb-2 mt-6 font-display text-xs text-ink-muted">Rates</h4>
            <div className="grid gap-4 md:grid-cols-3">
              <Field label={ssdLabel} htmlFor="rate-ssdSoftware" hint={s.ssdRateHint}>
                <MoneyField id="rate-ssdSoftware" ariaLabel={ssdLabel} value={s.rates.tempSsdSoftware}
                  onChange={(raw) => s.rates.handleSsdSoftwareChange({ target: { value: raw } })}
                  onBlur={s.rates.handleSsdSoftwareBlur} step={0.1} />
              </Field>
              <Field label={hddLabel} htmlFor="rate-hddSoftware" hint={s.hddRateHint}>
                <MoneyField id="rate-hddSoftware" ariaLabel={hddLabel} value={s.rates.tempHddSoftware}
                  onChange={(raw) => s.rates.handleHddSoftwareChange({ target: { value: raw } })}
                  onBlur={s.rates.handleHddSoftwareBlur} step={0.1} />
              </Field>
              <Field label="Discount Months" htmlFor="rate-discountMonths">
                <MoneyField id="rate-discountMonths" ariaLabel="Discount Months" prefix="#" value={String(s.rates.tempDiscountMonths)}
                  onChange={(raw) => s.rates.handleDiscountMonthsChange({ target: { value: raw } })}
                  onBlur={s.rates.handleDiscountMonthsBlur} />
              </Field>
            </div>
            <div className="mt-3"><RegisteredToggle testId="registered-rates" checked={s.registered} onToggle={s.onRegisteredToggle} /></div>
          </>
        )}
      </Section.Body>
    </Section.Root>
  );
}
